import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Users, BarChart3 } from 'lucide-react';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  const scrollToCourses = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    } else if (location.pathname !== '/') {
      window.location.href = '/#courses-section';
    }
    setIsMobileMenuOpen(false);
  };

  const navLinkClasses = (path: string) => `
    px-4 py-2 text-sm font-medium rounded-full transition-all duration-300
    ${isCurrentPath(path)
      ? 'bg-white/15 text-white shadow-lg shadow-white/10'
      : 'text-gray-200 hover:bg-white/10 hover:text-white hover:shadow-lg hover:shadow-white/5'
    }
  `;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-b border-white/5" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center space-x-2 group">
                <span className="text-2xl font-extrabold bg-gradient-to-r from-white via-indigo-200 to-blue-200 bg-clip-text text-transparent transition-all duration-300 group-hover:from-white group-hover:via-indigo-300 group-hover:to-blue-300">
                  منصة أ. عمر الخولي
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex md:items-center md:mr-6 md:space-x-2">
              <Link to="/" className={navLinkClasses('/')}>
                الرئيسية
              </Link>
              {!isAuthenticated && (
                <button
                  onClick={scrollToCourses}
                  className={`${navLinkClasses('/courses')} cursor-pointer`}
                >
                  الدورات
                </button>
              )}
              {isAuthenticated && (
                <>
                  <Link to="/enrolled-courses" className={navLinkClasses('/enrolled-courses')}>
                    دوراتي
                  </Link>
                  <Link to="/my-lectures" className={navLinkClasses('/my-lectures')}>
                    محاضراتي
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Right: Statistics + Parent Monitoring + Night mode + Auth */}
          <div className="flex items-center gap-3 justify-end">
            {/* Statistics and Parent Monitoring Links - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-2 space-x-reverse">
              <Link to="/parent-monitoring" className={navLinkClasses('/parent-monitoring')}>
                <Users className="w-3 h-3 sm:w-4 sm:h-4 inline-block ml-1.5" />
                متابعة ولي الأمر
              </Link>
            </div>

            {/* Auth Section */}
            <div className="hidden md:flex md:items-center">
              {isAuthenticated ? (
                <>
                  <div className="relative mr-3 flex items-center space-x-3">
                    <span className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white/5 rounded-full border border-white/10 backdrop-blur-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-300" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      <span className="bg-gradient-to-r from-white via-indigo-200 to-blue-200 bg-clip-text text-transparent">
                      مرحباً، {currentUser?.name}
                      </span>
                    </span>
                    <button
                      onClick={logout}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-500/20 rounded-full hover:bg-red-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10"
                    >
                      تسجيل الخروج
                    </button>
                  </div>
                </>
              ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-white hover:bg-white/10 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-white/10"
                >
                  تسجيل الدخول
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-indigo-500 to-blue-500 text-white hover:from-indigo-600 hover:to-blue-600 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20"
                >
                  إنشاء حساب
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex items-center justify-center p-2 rounded-full text-white hover:bg-white/10 transition-all duration-300"
          >
            <span className="sr-only">فتح القائمة الرئيسية</span>
            {!isMobileMenuOpen ? (
              <svg
                className="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            ) : (
              <svg
                className="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div 
        className={`md:hidden backdrop-blur-md bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-t border-white/5 transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-lg transition-all duration-300"
            >
              الرئيسية
            </Link>
            
            {!isAuthenticated && (
              <button
                onClick={scrollToCourses}
              className="block w-full text-right px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-lg transition-all duration-300"
              >
                الدورات
              </button>
            )}
            {isAuthenticated && (
              <>
                <Link
                  to="/enrolled-courses"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-lg transition-all duration-300"
                >
                  دوراتي
                </Link>
                <Link
                  to="/my-lectures"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-lg transition-all duration-300"
                >
                  محاضراتي
                </Link>
              </>
            )}
            
            {/* Parent Monitoring Link */}
            <Link
              to="/parent-monitoring"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-lg transition-all duration-300"
            >
              <Users className="w-4 h-4" />
              متابعة ولي الأمر
            </Link>
            

            {isAuthenticated ? (
              <>
              <div className="flex items-center gap-2 px-3 py-2 text-base font-medium bg-white/5 rounded-lg border border-white/10 backdrop-blur-sm mx-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-300" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="bg-gradient-to-r from-white via-indigo-200 to-blue-200 bg-clip-text text-transparent">
                  مرحباً، {currentUser?.name}
                </span>
                </div>
                <button
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-right px-3 py-2 text-base font-medium text-white bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all duration-300"
                >
                  تسجيل الخروج
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-base font-medium text-white hover:bg-white/10 rounded-lg transition-all duration-300"
                >
                  تسجيل الدخول
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-base font-medium text-white bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 rounded-lg transition-all duration-300"
                >
                  إنشاء حساب
                </Link>
              </>
            )}
          </div>
        </div>
    </nav>
  );
};

export default Navbar; 